import { motion } from 'framer-motion'
import { TrendingDown, Droplets, ShoppingCart, AlertTriangle, ArrowRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const ResourceCrisisSection = () => {
  const crisisData = [
    {
      icon: TrendingDown,
      title: '20–35% Margin Loss',
      description: 'Tunisian enterprises lose significant margins to unmonitored passive infrastructure.',
      color: 'from-red-500 to-orange-500',
    },
    {
      icon: AlertTriangle,
      title: 'Energy Waste',
      description: 'Punitive "Tranche Trap" tariffs from STEG penalize consumption spikes.',
      color: 'from-yellow-500 to-orange-500',
    },
    {
      icon: Droplets,
      title: 'Water & Retail',
      description: 'Millions of liters lost to leaks; inventory shrinkage from undetected theft.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: ShoppingCart,
      title: 'Inventory Shrinkage',
      description: 'Traditional POS systems miss sophisticated theft patterns and concealment.',
      color: 'from-purple-500 to-pink-500',
    },
  ]

  return (
    <section className="py-20 bg-secondary/50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="section-container relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
            The Resource <span className="gradient-text">Crisis</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Turning Leaks into Profits — Industrial waste is silently destroying your margins
          </p>
        </motion.div>

        {/* Crisis Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {crisisData.map((crisis, index) => (
            <motion.div
              key={crisis.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <Card className="h-full hover:border-primary/30 transition-all duration-300">
                <CardContent className="p-6">
                  <div className={`w-14 h-14 bg-gradient-to-br ${crisis.color} rounded-xl flex items-center justify-center mb-4`}>
                    <crisis.icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {crisis.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {crisis.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-muted-foreground mb-6">
            Don't let unmonitored infrastructure drain your profits
          </p>
          <Button size="lg" className="rounded-full" asChild>
            <a href="#products" className="inline-flex items-center space-x-2">
              <span>Discover Our Solutions</span>
              <ArrowRight size={20} />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  )
}

export default ResourceCrisisSection
